// components/AI_Chat/MessageBubble.tsx
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ReactComponent as IconCopy } from "assets/img/icons/icon_copy2.svg";
import { ReactComponent as IconCheck } from "assets/img/icons/icon_Check.svg";
import React, { useState, useEffect, useRef, memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import imgKuma from "assets/img/img_Kuma.png";

// Kuma écrit tantôt un lien markdown [ID Locker](#ID_Locker#), tantôt le jeton nu #ID_Locker#.
// Seul le premier cas produit une balise <a>, donc seul lui était transformé en lien : un jeton
// nu s'affichait tel quel à l'utilisateur (bug constaté sur #ID_Assistant#). On enveloppe donc
// les jetons nus dans la syntaxe markdown avant le rendu.
// Le groupe optionnel "](" sert uniquement à reconnaître un jeton DÉJÀ dans un lien markdown,
// pour ne pas le réécrire.
const TOOL_TOKEN_RE = /(\]\()?#([A-Za-z][A-Za-z0-9_]*)#/g;

function linkifyToolTokens(text: string, toolLinks?: { [key: string]: string }): string {
  if (!toolLinks) return text;
  return text.replace(TOOL_TOKEN_RE, (match, insideMarkdownLink, key) => {
    if (insideMarkdownLink) return match;
    if (!toolLinks[key]) return match;
    return `[${key.replace(/_/g, " ")}](#${key}#)`;
  });
}

interface MessageBubbleProps {
  role: "user" | "assistant" | string;
  content: string;
  toolLinks?: { [key: string]: string };
  shouldType?: boolean;
  typingSpeed?: number;
  onTypingComplete?: (id: string) => void;
  id?: string;
  forceScrollToBottom: any;
  isError?: boolean;
}

const MessageBubble = memo(({
  role,
  content,
  toolLinks,
  shouldType = false,
  typingSpeed = 15,
  onTypingComplete,
  id,
  forceScrollToBottom,
  isError = false,
}: MessageBubbleProps) => {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<"liked" | "disliked" | null>(null);
  const [displayedContent, setDisplayedContent] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const typingCompletedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const shouldStartTyping = role === "assistant" && shouldType === true && !typingCompletedRef.current;

    if (!shouldStartTyping) {
      if (typingCompletedRef.current || !shouldType) {
        setDisplayedContent(content);
      }
      return;
    }

    setDisplayedContent("");
    setIsTyping(true);

    let currentIndex = 0;

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (currentIndex <= content.length) {
        setDisplayedContent(content.substring(0, currentIndex));
        currentIndex++;
        if (forceScrollToBottom) forceScrollToBottom();
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsTyping(false);
        typingCompletedRef.current = true;
        if (onTypingComplete && id) onTypingComplete(id);
      }
    }, typingSpeed);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [content, shouldType, role, typingSpeed, onTypingComplete, id]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const textToDisplay = isTyping ? displayedContent : content;
  const showCursor = isTyping && textToDisplay !== content;
  const rawFinalText = showCursor ? textToDisplay + "|" : textToDisplay;
  const finalText = useMemo(() => linkifyToolTokens(rawFinalText, toolLinks), [rawFinalText, toolLinks]);

  const markdownComponents = {
    a: ({ href, children }: any) => {
      if (href?.startsWith("#") && href?.endsWith("#")) {
        const cleanLabel = href.slice(1, -1).trim();
        const url = toolLinks?.[cleanLabel];
        // Jeton inconnu : afficher le libellé seul plutôt qu'un lien mort vers "#".
        if (!url) return <>{children}</>;
        // href conservé pour l'accessibilité et le clic milieu ; le clic simple navigue
        // via React Router, sinon la SPA se rechargerait entièrement.
        return (
          <a
            href={url}
            className="tool-link"
            onClick={(event) => { event.preventDefault(); navigate(url); }}
          >
            🔧 {children}
          </a>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" className="tool-link">{children}</a>;
    },
    p: ({ children }: any) => <p className="markdown-p">{children}</p>,
    strong: ({ children }: any) => <strong className="markdown-bold">{children}</strong>,
    h2: ({ children }: any) => <h2 className="markdown-h2">{children}</h2>,
    h3: ({ children }: any) => <h3 className="markdown-h3">{children}</h3>,
    ul: ({ children }: any) => <ul className="markdown-ul">{children}</ul>,
    ol: ({ children }: any) => <ol className="markdown-ol">{children}</ol>,
    li: ({ children }: any) => <li className="markdown-li">{children}</li>,
    blockquote: ({ children }: any) => <blockquote className="markdown-blockquote">{children}</blockquote>,
    pre: ({ children }: any) => <pre className="markdown-pre">{children}</pre>,
    code: ({ inline, children }: any) => {
      if (inline) return <code className="markdown-code-inline">{children}</code>;
      return <code>{children}</code>;
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`message-bubble ${role}`}
    >
      {role === "assistant" && (
        <div className="avatar">
          <div className="avatar-icon">
            <img src={imgKuma} alt="Kuma" />
          </div>
        </div>
      )}

      <div className="message-content">
        <div className={`bubble${isError ? " bubble--error" : ""}`}>
          <ReactMarkdown components={markdownComponents as any}>
            {finalText}
          </ReactMarkdown>
        </div>

        {role === "assistant" && !isTyping && !isError && (
          <div className="message-actions">
            <button
              onClick={handleCopy}
              className="action-icon-btn"
              title={copied ? "Copié !" : "Copier"}
            >
              {copied
                ? <IconCheck width={13} height={13} fill={"#008236"} />
                : <IconCopy width={13} height={13} fill={"currentColor"}/>
              }
            </button>

            <button
              onClick={() => setFeedback(feedback === "liked" ? null : "liked")}
              className={`action-icon-btn ${feedback === "liked" ? "liked" : ""}`}
              title="Utile"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
                <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
              </svg>
            </button>

            <button
              onClick={() => setFeedback(feedback === "disliked" ? null : "disliked")}
              className={`action-icon-btn ${feedback === "disliked" ? "disliked" : ""}`}
              title="Pas utile"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/>
                <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}, (prev, next) => {
  if (prev.shouldType !== next.shouldType) return false;
  if (prev.content !== next.content) return false;
  if (prev.id !== next.id) return false;
  if (prev.isError !== next.isError) return false;
  return true;
});

export default MessageBubble;
